<template>
  <div class="settings-view">
    <div class="content">
      <div class="page-title">
        <h1>Settings</h1>
        <p class="page-description">Configure integrations and storage providers</p>
      </div>

      <div class="tabs">
        <button v-if="isAdmin" :class="['tab', { active: activeTab === 'usage' }]" @click="activeTab = 'usage'">
          <AppIcon name="chart-pie" />Usage
        </button>
        <button :class="['tab', { active: activeTab === 'storage' }]" @click="activeTab = 'storage'">
          <AppIcon name="cloud" />Storage
        </button>
        <button :class="['tab', { active: activeTab === 'ai' }]" @click="activeTab = 'ai'">
          <AppIcon name="robot" />AI Services
        </button>
        <button :class="['tab', { active: activeTab === 'webhooks' }]" @click="activeTab = 'webhooks'">
          <AppIcon name="link" />Webhooks
        </button>
        <button :class="['tab', { active: activeTab === 'customFields' }]" @click="activeTab = 'customFields'">
          <AppIcon name="file-alt" />Custom Fields
        </button>
      </div>

      <!-- Storage Tab -->
      <div v-if="activeTab === 'storage'" class="tab-content">
        <div class="providers-list">
          <div class="info-card">
            <AppIcon name="info-circle" />
            <p>Configure cloud storage to save your exports.</p>
          </div>

          <!-- AWS S3 -->
          <div :class="['provider-card', { active: storage.activeProvider === 'aws', configured: storage.aws?.configured }]">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon aws">S3</div>
                <div>
                  <h3>Amazon S3</h3>
                  <p class="provider-desc">Store files in your own AWS S3 bucket</p>
                </div>
              </div>
              <div class="provider-status">
                <span v-if="storage.activeProvider === 'aws'" class="badge active">Active</span>
                <span v-else-if="storage.aws?.configured" class="badge configured">Configured</span>
              </div>
            </div>
            <div class="provider-body">
              <div v-if="editingProvider === 'aws'" class="provider-form">
                <div class="form-group">
                  <label>Access Key ID</label>
                  <input type="text" v-model="awsConfig.accessKeyId" placeholder="AKIAIOSFODNN7EXAMPLE" class="form-input" />
                </div>
                <div class="form-group">
                  <label>Secret Access Key</label>
                  <div class="input-with-toggle">
                    <input :type="showSecrets.awsSecret ? 'text' : 'password'" v-model="awsConfig.secretAccessKey" :placeholder="storage.aws?.configured ? 'Enter new key to replace existing' : 'Secret Key'" class="form-input" />
                    <button type="button" class="btn-toggle-visibility" @click="showSecrets.awsSecret = !showSecrets.awsSecret"><AppIcon :name="showSecrets.awsSecret ? 'eye-slash' : 'eye'" /></button>
                  </div>
                  <p v-if="storage.aws?.configured && !awsConfig.secretAccessKey" class="form-hint success"><AppIcon name="check-circle" />Secret key is configured.</p>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Bucket Name</label>
                    <input type="text" v-model="awsConfig.bucket" placeholder="my-bucket" class="form-input" />
                  </div>
                  <div class="form-group">
                    <label>Region</label>
                    <select v-model="awsConfig.region" class="form-select">
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-east-2">US East (Ohio)</option>
                      <option value="us-west-1">US West (N. California)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">EU (Ireland)</option>
                      <option value="eu-central-1">EU (Frankfurt)</option>
                      <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                      <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                    </select>
                  </div>
                </div>
                <div class="form-group">
                  <label>CDN URL <span class="optional">(optional)</span></label>
                  <input type="text" v-model="awsConfig.cdnUrl" placeholder="https://d1234567890.cloudfront.net" class="form-input" />
                  <p class="form-hint"><AppIcon name="info-circle" />CloudFront distribution URL for faster delivery.</p>
                </div>
                <div class="form-actions">
                  <button class="btn-secondary" @click="cancelEdit">Cancel</button>
                  <button class="btn-secondary" @click="testProvider('aws', awsConfig)" :disabled="testing">{{ testing ? 'Testing...' : 'Test Connection' }}</button>
                  <button class="btn-primary" @click="saveProvider('aws', awsConfig)" :disabled="saving">{{ saving ? 'Saving...' : 'Save & Activate' }}</button>
                </div>
                <div v-if="testResult" :class="['test-result', testResult.success ? 'success' : 'error']"><AppIcon :name="testResult.success ? 'check-circle' : 'times-circle'" /> {{ testResult.message }}</div>
              </div>
              <div v-else class="provider-actions">
                <button class="btn-secondary" @click="editProvider('aws')">{{ storage.aws?.configured ? 'Edit' : 'Configure' }}</button>
                <button v-if="storage.aws?.configured && storage.activeProvider !== 'aws'" class="btn-primary" @click="setActiveProvider('aws')" :disabled="saving">Activate</button>
                <button v-if="storage.aws?.configured || storage.aws?.exists" class="btn-danger" @click="confirmDeleteProvider('aws')" :disabled="saving">Delete</button>
              </div>
            </div>
          </div>

          <!-- DigitalOcean Spaces -->
          <div :class="['provider-card', { active: storage.activeProvider === 'digitalocean', configured: storage.digitalocean?.configured }]">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon do">DO</div>
                <div>
                  <h3>DigitalOcean Spaces</h3>
                  <p class="provider-desc">Store files in your own DO Space</p>
                </div>
              </div>
              <div class="provider-status">
                <span v-if="storage.activeProvider === 'digitalocean'" class="badge active">Active</span>
                <span v-else-if="storage.digitalocean?.configured" class="badge configured">Configured</span>
              </div>
            </div>
            <div class="provider-body">
              <div v-if="editingProvider === 'digitalocean'" class="provider-form">
                <div class="form-group">
                  <label>Spaces Access Key</label>
                  <input type="text" v-model="doConfig.accessKeyId" placeholder="Access Key" class="form-input" />
                </div>
                <div class="form-group">
                  <label>Spaces Secret Key</label>
                  <div class="input-with-toggle">
                    <input :type="showSecrets.doSecret ? 'text' : 'password'" v-model="doConfig.secretAccessKey" :placeholder="storage.digitalocean?.configured ? 'Enter new key to replace existing' : 'Secret Key'" class="form-input" />
                    <button type="button" class="btn-toggle-visibility" @click="showSecrets.doSecret = !showSecrets.doSecret"><AppIcon :name="showSecrets.doSecret ? 'eye-slash' : 'eye'" /></button>
                  </div>
                  <p v-if="storage.digitalocean?.configured && !doConfig.secretAccessKey" class="form-hint success"><AppIcon name="check-circle" />Secret key is configured.</p>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Space Name</label>
                    <input type="text" v-model="doConfig.bucket" placeholder="my-space" class="form-input" />
                  </div>
                  <div class="form-group">
                    <label>Region</label>
                    <select v-model="doConfig.region" class="form-select">
                      <option value="nyc3">New York 3</option>
                      <option value="sfo3">San Francisco 3</option>
                      <option value="ams3">Amsterdam 3</option>
                      <option value="sgp1">Singapore 1</option>
                      <option value="fra1">Frankfurt 1</option>
                    </select>
                  </div>
                </div>
                <div class="form-group">
                  <label>CDN URL <span class="optional">(optional)</span></label>
                  <input type="text" v-model="doConfig.cdnUrl" placeholder="https://my-space.nyc3.cdn.digitaloceanspaces.com" class="form-input" />
                </div>
                <div class="form-actions">
                  <button class="btn-secondary" @click="cancelEdit">Cancel</button>
                  <button class="btn-secondary" @click="testProvider('digitalocean', doConfig)" :disabled="testing">{{ testing ? 'Testing...' : 'Test Connection' }}</button>
                  <button class="btn-primary" @click="saveProvider('digitalocean', doConfig)" :disabled="saving">{{ saving ? 'Saving...' : 'Save & Activate' }}</button>
                </div>
                <div v-if="testResult" :class="['test-result', testResult.success ? 'success' : 'error']"><AppIcon :name="testResult.success ? 'check-circle' : 'times-circle'" /> {{ testResult.message }}</div>
              </div>
              <div v-else class="provider-actions">
                <button class="btn-secondary" @click="editProvider('digitalocean')">{{ storage.digitalocean?.configured ? 'Edit' : 'Configure' }}</button>
                <button v-if="storage.digitalocean?.configured && storage.activeProvider !== 'digitalocean'" class="btn-primary" @click="setActiveProvider('digitalocean')" :disabled="saving">Activate</button>
                <button v-if="storage.digitalocean?.configured || storage.digitalocean?.exists" class="btn-danger" @click="confirmDeleteProvider('digitalocean')" :disabled="saving">Delete</button>
              </div>
            </div>
          </div>

          <div v-if="storage.activeProvider" class="deactivate-section">
            <button class="btn-text" @click="confirmDeactivateStorage"><AppIcon name="times-circle" />Deactivate cloud storage (use local storage)</button>
          </div>
        </div>
      </div>

      <!-- AI Services Tab -->
      <div v-if="activeTab === 'ai'" class="tab-content">

        <!-- ── Transcription section ───────────────────────────────────── -->
        <div class="ai-section-header">
          <h3 class="ai-section-title">Transcription</h3>
          <p class="ai-section-desc">One provider can be active at a time for audio transcription and AI features</p>
        </div>
        <div class="providers-list">
          <div :class="['provider-card', { configured: openai.configured, active: activeAIProvider === 'openai' }]">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon ai">AI</div>
                <div>
                  <h3>OpenAI</h3>
                  <p class="provider-desc">Transcription (Whisper) and AI-powered features</p>
                </div>
              </div>
              <div class="provider-status" v-if="openai.configured && !openai.editing">
                <span v-if="activeAIProvider === 'openai'" class="badge active">Active</span>
                <span v-else class="badge configured">Configured</span>
              </div>
            </div>
            <div class="provider-body">
              <div v-if="openai.editing" class="provider-form">
                <div class="form-group">
                  <label>API Key</label>
                  <div class="input-with-toggle">
                    <input :type="openai.showKey ? 'text' : 'password'" v-model="openai.form.apiKey" :placeholder="openai.configured ? 'Enter new key to replace existing' : 'Enter your OpenAI API key'" class="form-input" />
                    <button type="button" class="btn-toggle-visibility" @click="openai.showKey = !openai.showKey"><AppIcon :name="openai.showKey ? 'eye-slash' : 'eye'" /></button>
                  </div>
                  <p v-if="openai.configured" class="form-hint success"><AppIcon name="check-circle" />API key is configured. Leave blank to keep current key.</p>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Transcription Model</label>
                    <select v-model="openai.form.transcriptionModel" class="form-select"><option value="whisper-1">Whisper v1</option></select>
                  </div>
                  <div class="form-group">
                    <label>Snippets Model</label>
                    <select v-model="openai.form.snippetsModel" class="form-select">
                      <option value="gpt-4o-mini">GPT-4o Mini (Recommended)</option>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>
              </div>
              <div class="form-actions">
                <button class="btn-secondary" @click="cancelOpenAIEdit">Cancel</button>
                <button class="btn-secondary" @click="testOpenAI" :disabled="openai.testing">{{ openai.testing ? 'Testing...' : 'Test Connection' }}</button>
                <button class="btn-primary" @click="saveOpenAI" :disabled="openai.saving">{{ openai.saving ? 'Saving...' : 'Save' }}</button>
              </div>
              <div v-if="openai.testResult" :class="['test-result', openai.testResult.success ? 'success' : 'error']"><AppIcon :name="openai.testResult.success ? 'check-circle' : 'times-circle'" /> {{ openai.testResult.message || openai.testResult.error }}</div>
              <div v-if="openai.error" class="form-error"><AppIcon name="exclamation-circle" /> {{ openai.error }}</div>
            </div>
            <div v-else class="provider-actions">
              <button class="btn-secondary" @click="editOpenAI">Edit</button>
              <button v-if="openai.configured && activeAIProvider !== 'openai'" class="btn-primary" @click="setActiveAIProvider('openai')" :disabled="aiProviderSaving">Activate</button>
              <button v-if="openai.configured && activeAIProvider !== 'openai'" class="btn-danger" @click="confirmDeleteOpenAI">Delete</button>
            </div>
            </div>
          </div>

          <!-- Gemini card -->
          <div :class="['provider-card', { configured: gemini.configured, active: activeAIProvider === 'gemini' }]">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon gemini">G</div>
                <div>
                  <h3>Google Gemini</h3>
                  <p class="provider-desc">Transcription via Gemini audio models</p>
                </div>
              </div>
              <div class="provider-status" v-if="!gemini.editing">
                <span v-if="activeAIProvider === 'gemini'" class="badge active">Active</span>
                <span v-else-if="gemini.configured" class="badge configured">Configured</span>
              </div>
            </div>
            <div class="provider-body">
              <div v-if="gemini.editing" class="provider-form">
                <div class="form-group">
                  <label>API Key</label>
                  <div class="input-with-toggle">
                    <input :type="gemini.showKey ? 'text' : 'password'" v-model="gemini.form.apiKey" :placeholder="gemini.configured ? 'Enter new key to replace existing' : 'Enter your Gemini API key'" class="form-input" />
                    <button type="button" class="btn-toggle-visibility" @click="gemini.showKey = !gemini.showKey"><AppIcon :name="gemini.showKey ? 'eye-slash' : 'eye'" /></button>
                  </div>
                  <p v-if="gemini.configured" class="form-hint success"><AppIcon name="check-circle" />API key is configured. Leave blank to keep current key.</p>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Transcription Model</label>
                    <select v-model="gemini.form.transcriptionModel" class="form-select">
                      <option value="gemini-2.5-flash">Gemini 2.5 Flash (Recommended)</option>
                      <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
                    </select>
                  </div>
                </div>
                <div class="form-actions">
                  <button class="btn-secondary" @click="cancelGeminiEdit">Cancel</button>
                  <button class="btn-secondary" @click="testGemini" :disabled="gemini.testing">{{ gemini.testing ? 'Testing...' : 'Test Connection' }}</button>
                  <button class="btn-primary" @click="saveGemini" :disabled="gemini.saving">{{ gemini.saving ? 'Saving...' : 'Save' }}</button>
                </div>
                <div v-if="gemini.testResult" :class="['test-result', gemini.testResult.success ? 'success' : 'error']"><AppIcon :name="gemini.testResult.success ? 'check-circle' : 'times-circle'" /> {{ gemini.testResult.message || gemini.testResult.error }}</div>
                <div v-if="gemini.error" class="form-error"><AppIcon name="exclamation-circle" /> {{ gemini.error }}</div>
              </div>
              <div v-else class="provider-actions">
                <button class="btn-secondary" @click="editGemini">{{ gemini.configured ? 'Edit' : 'Configure' }}</button>
                <button v-if="gemini.configured && activeAIProvider !== 'gemini'" class="btn-primary" @click="setActiveAIProvider('gemini')" :disabled="aiProviderSaving">Activate</button>
                <button v-if="gemini.configured && activeAIProvider !== 'gemini'" class="btn-danger" @click="confirmDeleteGemini">Delete</button>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Voice Generation section ───────────────────────────────── -->
        <div class="ai-section-divider"></div>
        <div class="ai-section-header">
          <h3 class="ai-section-title">Voice Generation</h3>
          <p class="ai-section-desc">Clone and generate speech from a DJ voice sample</p>
        </div>
        <div class="providers-list">
          <div :class="['provider-card', { configured: elevenlabs.configured, active: elevenlabs.configured }]">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon elevenlabs">11</div>
                <div>
                  <h3>ElevenLabs</h3>
                  <p class="provider-desc">Voice cloning and text-to-speech generation</p>
                </div>
              </div>
              <div class="provider-status" v-if="!elevenlabs.editing">
                <span v-if="elevenlabs.configured" class="badge active">Active</span>
              </div>
            </div>
            <div class="provider-body">
              <div v-if="elevenlabs.editing" class="provider-form">
                <div class="form-group">
                  <label>API Key</label>
                  <div class="input-with-toggle">
                    <input :type="elevenlabs.showKey ? 'text' : 'password'" v-model="elevenlabs.form.apiKey" :placeholder="elevenlabs.configured ? 'Enter new key to replace existing' : 'Enter your ElevenLabs API key'" class="form-input" />
                    <button type="button" class="btn-toggle-visibility" @click="elevenlabs.showKey = !elevenlabs.showKey"><AppIcon :name="elevenlabs.showKey ? 'eye-slash' : 'eye'" /></button>
                  </div>
                  <p v-if="elevenlabs.configured" class="form-hint success"><AppIcon name="check-circle" />API key is configured. Leave blank to keep current key.</p>
                </div>
                <div class="form-actions">
                  <button class="btn-secondary" @click="cancelElevenLabsEdit">Cancel</button>
                  <button class="btn-secondary" @click="testElevenLabs" :disabled="elevenlabs.testing">{{ elevenlabs.testing ? 'Testing...' : 'Test Connection' }}</button>
                  <button class="btn-primary" @click="saveElevenLabs" :disabled="elevenlabs.saving">{{ elevenlabs.saving ? 'Saving...' : 'Save' }}</button>
                </div>
                <div v-if="elevenlabs.testResult" :class="['test-result', elevenlabs.testResult.success ? 'success' : 'error']"><AppIcon :name="elevenlabs.testResult.success ? 'check-circle' : 'times-circle'" /> {{ elevenlabs.testResult.message || elevenlabs.testResult.error }}</div>
                <div v-if="elevenlabs.error" class="form-error"><AppIcon name="exclamation-circle" /> {{ elevenlabs.error }}</div>
              </div>
              <div v-else class="provider-actions">
                <button class="btn-secondary" @click="editElevenLabs">{{ elevenlabs.configured ? 'Edit' : 'Configure' }}</button>
                <button v-if="elevenlabs.configured" class="btn-danger" @click="confirmDeleteElevenLabs">Delete</button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Webhooks Tab -->
      <div v-if="activeTab === 'webhooks'" class="tab-content">
        <div class="section-header">
          <div>
            <h3 class="section-title">
              Webhooks
              <button class="help-icon" @click="showPayloadDocs = true" title="View payload documentation">
                <span class="help-icon-circle">?</span>
              </button>
            </h3>
            <p class="section-desc">Configure HTTP webhooks to receive event notifications</p>
          </div>
          <button class="btn-primary" @click="openWebhookModal()">+ Add Webhook</button>
        </div>

        <div v-if="webhooks.length === 0" class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          <p>No webhooks configured</p>
          <p class="empty-hint">Add a webhook to receive event notifications when exports start, complete, or fail.</p>
        </div>

        <div v-else class="webhooks-list">
          <div v-for="webhook in webhooks" :key="webhook._id" class="webhook-card">
            <div class="webhook-header">
              <div class="webhook-info">
                <h4>{{ webhook.name }}</h4>
                <p class="webhook-url">{{ webhook.url }}</p>
              </div>
              <div class="webhook-actions">
                <span :class="['badge', webhook.enabled ? 'enabled' : 'disabled']">
                  {{ webhook.enabled ? 'Enabled' : 'Disabled' }}
                </span>
                <button class="btn-icon" @click="testWebhookAction(webhook)" title="Test">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </button>
                <button class="btn-icon" @click="openWebhookModal(webhook)" title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button class="btn-icon" @click="confirmDeleteWebhook(webhook)" title="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="webhook-events">
              <span v-for="event in webhook.events" :key="event" class="event-tag">
                {{ formatEventName(event) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Webhook Modal -->
        <div v-if="webhookModal.show" class="modal-overlay">
          <div class="modal settings-modal">
            <div class="settings-modal-header">
              <div class="settings-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <h2>{{ webhookModal.editing ? 'Edit Webhook' : 'Add Webhook' }}</h2>
              </div>
              <button class="settings-modal-close" @click="webhookModal.show = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="settings-modal-body">
              <div class="settings-toggle-section">
                <div class="settings-checkbox-item" @click="webhookModal.enabled = !webhookModal.enabled">
                  <div class="custom-checkbox" :class="{ checked: webhookModal.enabled }">
                    <svg v-if="webhookModal.enabled" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  </div>
                  <span>Enable Webhook</span>
                </div>
                <p class="settings-toggle-hint">When enabled, this webhook will receive event notifications.</p>
              </div>
              <div class="settings-form-group">
                <label>Name</label>
                <input type="text" v-model="webhookModal.name" placeholder="Slack Notifications" />
              </div>
              <div class="settings-form-group">
                <label>URL</label>
                <input type="url" v-model="webhookModal.url" placeholder="https://hooks.slack.com/..." />
              </div>
              <div class="settings-form-group">
                <label>Secret <span class="optional">(optional)</span></label>
                <input type="text" v-model="webhookModal.secret" placeholder="For HMAC signature verification" />
                <p class="settings-help-text">If set, requests will include X-Webhook-Signature header</p>
              </div>
              <div class="settings-form-group">
                <label>Events</label>
                <div class="settings-checkbox-grid">
                  <div v-for="event in availableEvents" :key="event.value" class="settings-checkbox-item" @click="toggleEvent(event.value)">
                    <div class="custom-checkbox" :class="{ checked: webhookModal.events.includes(event.value) }">
                      <svg v-if="webhookModal.events.includes(event.value)" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                    <span>{{ event.label }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="settings-modal-footer">
              <button class="btn-secondary" @click="webhookModal.show = false">Cancel</button>
              <button class="btn-primary" @click="saveWebhook" :disabled="!webhookModal.name || !webhookModal.url || webhookModal.events.length === 0">
                {{ webhookModal.editing ? 'Update' : 'Create' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Payload Documentation Modal -->
        <div v-if="showPayloadDocs" class="modal-overlay">
          <div class="modal settings-modal payload-docs-modal">
            <div class="settings-modal-header">
              <div class="settings-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <h2>Webhook Payload Reference</h2>
              </div>
              <button class="settings-modal-close" @click="showPayloadDocs = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="settings-modal-body payload-docs-body">
              <p class="payload-intro">All webhooks are sent as HTTP POST requests with <code>Content-Type: application/json</code>. If a secret is configured, the signature is included in the <code>X-Webhook-Signature</code> header as <code>sha256=&lt;hmac&gt;</code>.</p>
              <div class="payload-section">
                <h4>Export Events</h4>
                <div class="payload-event">
                  <span class="event-tag">export.started</span>
                  <pre><code>{
  "event": "export.started",
  "timestamp": "2026-02-23T21:30:00.000Z",
  "export": {
    "id": "exp-1234567890-abc123def",
    "title": "Morning Show",
    "stationId": "station_001",
    "stationSlug": "wkrp-fm",
    "stationName": "WKRP FM",
    "duration": 3600000,
    "isMultiCut": false,
    "cutsCount": 1,
    "customFields": { }
  }
}</code></pre>
                </div>
                <div class="payload-event">
                  <span class="event-tag">export.complete</span>
                  <pre><code>{
  "event": "export.complete",
  "timestamp": "2026-02-23T21:35:00.000Z",
  "export": {
    "id": "exp-1234567890-abc123def",
    "title": "Morning Show",
    "stationId": "station_001",
    "stationSlug": "wkrp-fm",
    "stationName": "WKRP FM",
    "duration": 3600000,
    "isMultiCut": false,
    "cutsCount": 1,
    "customFields": { },
    "fileSize": 57600000,
    "fileUrl": "https://cdn.example.com/..."
  }
}</code></pre>
                </div>
                <div class="payload-event">
                  <span class="event-tag">export.failed</span>
                  <pre><code>{
  "event": "export.failed",
  "timestamp": "2026-02-23T21:35:00.000Z",
  "export": {
    "id": "exp-1234567890-abc123def",
    "title": "Morning Show",
    "stationId": "station_001",
    "stationSlug": "wkrp-fm",
    "stationName": "WKRP FM",
    "customFields": { }
  },
  "error": "No audio segments found"
}</code></pre>
                </div>
              </div>
            </div>
            <div class="settings-modal-footer">
              <button class="btn-primary" @click="showPayloadDocs = false">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Custom Fields Tab -->
      <div v-if="activeTab === 'customFields'" class="tab-content">
        <div class="section-header">
          <div>
            <h3 class="section-title">Custom Fields</h3>
            <p class="section-desc">Define custom fields for exports</p>
          </div>
          <button class="btn-primary" @click="openCustomFieldModal()">+ Add Field</button>
        </div>

        <div v-if="customFields.length === 0" class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          <p>No custom fields defined</p>
          <p class="empty-hint">Custom fields let you add additional metadata to exports, which are included in webhook payloads.</p>
        </div>

        <div v-else class="webhooks-list">
          <div v-for="field in customFields" :key="field._id" class="webhook-card">
            <div class="webhook-header">
              <div class="webhook-info">
                <h4>{{ field.name }}</h4>
                <p class="webhook-url">{{ field.key }}</p>
                <div class="field-tags">
                  <span v-for="type in field.appliesTo" :key="type" class="event-tag">{{ formatAppliesTo(type) }}</span>
                </div>
              </div>
              <div class="webhook-actions">
                <span class="badge type-badge">{{ field.type }}</span>
                <span v-if="field.required" class="badge required-badge">Required</span>
                <button class="btn-icon" @click="openCustomFieldModal(field)" title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button class="btn-icon" @click="confirmDeleteCustomField(field)" title="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="field.type === 'dropdown' && field.options && field.options.length > 0" class="webhook-events">
              <span v-for="opt in field.options" :key="opt" class="event-tag">{{ opt }}</span>
            </div>
          </div>
        </div>

        <!-- Custom Field Modal -->
        <div v-if="customFieldModal.show" class="modal-overlay">
          <div class="modal settings-modal">
            <div class="settings-modal-header">
              <div class="settings-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <h2>{{ customFieldModal.editing ? 'Edit Custom Field' : 'Add Custom Field' }}</h2>
              </div>
              <button class="settings-modal-close" @click="customFieldModal.show = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="settings-modal-body">
              <div class="settings-form-group">
                <label>Name</label>
                <input type="text" v-model="customFieldModal.name" placeholder="Show Name" />
              </div>
              <div class="settings-form-group">
                <label>Key</label>
                <input type="text" v-model="customFieldModal.key" placeholder="show_name" :disabled="customFieldModal.editing" />
                <p class="settings-help-text">Used in API and webhooks. Cannot be changed after creation.</p>
              </div>
              <div class="settings-form-group">
                <label>Type</label>
                <select v-model="customFieldModal.type">
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>
              <div v-if="customFieldModal.type === 'dropdown'" class="settings-form-group">
                <label>Options</label>
                <input type="text" v-model="customFieldModal.optionsText" placeholder="Option 1, Option 2, Option 3" />
                <p class="settings-help-text">Comma-separated list of options</p>
              </div>
              <div class="settings-form-group">
                <label>Applies To</label>
                <div class="applies-to-checkboxes">
                  <div class="settings-checkbox-item" @click="customFieldModal.appliesToExports = !customFieldModal.appliesToExports">
                    <div class="custom-checkbox" :class="{ checked: customFieldModal.appliesToExports }">
                      <svg v-if="customFieldModal.appliesToExports" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                    <span>Exports</span>
                  </div>
                </div>
              </div>
              <div class="settings-toggle-section">
                <div class="settings-checkbox-item" @click="customFieldModal.required = !customFieldModal.required">
                  <div class="custom-checkbox" :class="{ checked: customFieldModal.required }">
                    <svg v-if="customFieldModal.required" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  </div>
                  <span>Required</span>
                </div>
                <p class="settings-toggle-hint">If enabled, this field must be filled when creating exports.</p>
              </div>
            </div>
            <div class="settings-modal-footer">
              <button class="btn-secondary" @click="customFieldModal.show = false">Cancel</button>
              <button class="btn-primary" @click="saveCustomField" :disabled="!customFieldModal.name || !customFieldModal.key || !customFieldModal.appliesToExports">
                {{ customFieldModal.editing ? 'Update' : 'Create' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    <!-- Usage Tab (Admin Only) -->
    <div v-if="activeTab === 'usage' && isAdmin" class="tab-content">
      <div class="providers-list">
        <div class="info-card">
          <AppIcon name="info-circle" />
          <p>View export storage usage across all stations. Storage totals are updated automatically when exports are created or deleted.</p>
        </div>

        <div v-if="storageUsage.loading" class="provider-card">
          <div class="provider-header" style="justify-content: center; padding: 48px;">
            <AppIcon name="spinner" spin />
          </div>
        </div>

        <template v-else>
          <!-- Summary Card -->
          <div class="provider-card">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon usage"><AppIcon name="database" /></div>
                <div>
                  <h3>Total Usage</h3>
                  <p class="provider-desc">Combined storage across all stations</p>
                </div>
              </div>
              <div class="provider-status">
                <button class="btn-secondary btn-sm" @click="loadStorageUsage" :disabled="storageUsage.loading">
                  <AppIcon name="sync-alt" />Refresh
                </button>
              </div>
            </div>
            <div class="provider-body">
              <div class="usage-stats-row">
                <div class="usage-stat-item">
                  <span class="usage-stat-value">{{ formatSize(storageUsage.totalStorage) }}</span>
                  <span class="usage-stat-label">Total Storage</span>
                </div>
                <div class="usage-stat-item">
                  <span class="usage-stat-value">{{ storageUsage.totalExports.toLocaleString() }}</span>
                  <span class="usage-stat-label">Total Exports</span>
                </div>
                <div class="usage-stat-item">
                  <span class="usage-stat-value">{{ storageUsage.stationCount }}</span>
                  <span class="usage-stat-label">Stations</span>
                </div>
              </div>
            </div>
          </div>

          <!-- By Station Card -->
          <div v-if="storageUsage.byStation.length > 0" class="provider-card">
            <div class="provider-header">
              <div class="provider-info">
                <div class="provider-icon stations"><AppIcon name="broadcast-tower" /></div>
                <div>
                  <h3>Usage by Station</h3>
                  <p class="provider-desc">Storage breakdown per station</p>
                </div>
              </div>
            </div>
            <div class="provider-body" style="padding: 0;">
              <table class="usage-table">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th>Exports</th>
                    <th>Storage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="station in storageUsage.byStation" :key="station.id">
                    <td>
                      <router-link :to="`/station/${station.slug}/exports`" class="station-link">
                        {{ station.name }}
                      </router-link>
                    </td>
                    <td>{{ station.exportCount.toLocaleString() }}</td>
                    <td>{{ formatSize(station.exportStorageTotal) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="provider-card">
            <div class="provider-body" style="text-align: center; padding: 48px;">
              <AppIcon name="folder-open" />
              <p style="color: #6b7280; margin: 0;">No exports yet</p>
            </div>
          </div>
        </template>
      </div>
    </div>
    </div>

    <!-- Alert Modal -->
    <div v-if="alertModal.show" class="modal-overlay">
      <div class="modal alert-modal">
        <button class="alert-modal-close" @click="alertModal.show = false">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div class="alert-modal-content">
          <div class="alert-modal-icon" :class="alertModal.variant">
            <svg v-if="alertModal.variant === 'success'" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <svg v-else-if="alertModal.variant === 'error'" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </div>
          <h3 class="alert-modal-title">{{ alertModal.title }}</h3>
          <p class="alert-modal-message">{{ alertModal.message }}</p>
          <button class="btn-primary alert-modal-btn" @click="alertModal.show = false">OK</button>
        </div>
      </div>
    </div>
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal">
        <div class="modal-header"><h3>{{ deleteModalTitle }}</h3><button class="close-btn" @click="showDeleteModal = false"><AppIcon name="times" /></button></div>
        <div class="modal-body"><p>{{ deleteModalMessage }}</p></div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showDeleteModal = false">Cancel</button>
          <button class="btn-danger" @click="executeDelete" :disabled="deleting">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authFetch, getWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook, getCustomFields, createCustomField, updateCustomField, deleteCustomField } from '../services/api'

export default {
  name: 'SettingsView',
  data() {
    return {
      activeTab: 'usage',
      storage: { activeProvider: null, aws: null, digitalocean: null },
      editingProvider: null,
      awsConfig: { accessKeyId: '', secretAccessKey: '', bucket: '', region: 'us-east-1', cdnUrl: '' },
      doConfig: { accessKeyId: '', secretAccessKey: '', bucket: '', region: 'nyc3', cdnUrl: '' },
      showSecrets: { awsSecret: false, doSecret: false },
      testing: false,
      saving: false,
      testResult: null,
      openai: { configured: false, editing: false, saving: false, testing: false, showKey: false, error: null, testResult: null, form: { apiKey: '', transcriptionModel: 'whisper-1', snippetsModel: 'gpt-4o-mini' } },
      gemini: { configured: false, editing: false, saving: false, testing: false, showKey: false, error: null, testResult: null, form: { apiKey: '', transcriptionModel: 'gemini-2.0-flash' } },
      elevenlabs: { configured: false, editing: false, saving: false, testing: false, showKey: false, error: null, testResult: null, form: { apiKey: '' } },
      activeAIProvider: null,
      aiProviderSaving: false,
      showDeleteModal: false,
      deleteModalTitle: '',
      deleteModalMessage: '',
      deleteAction: null,
      deleting: false,
      // Webhooks
      webhooks: [],
      webhookModal: {
        show: false,
        editing: null,
        name: '',
        url: '',
        secret: '',
        events: [],
        enabled: true
      },
      availableEvents: [
        { value: 'export.started', label: 'Export Started' },
        { value: 'export.complete', label: 'Export Complete' },
        { value: 'export.failed', label: 'Export Failed' }
      ],
      showPayloadDocs: false,
      // Custom Fields
      customFields: [],
      customFieldModal: {
        show: false,
        editing: null,
        name: '',
        key: '',
        type: 'text',
        appliesToExports: true,
        required: false,
        optionsText: ''
      },
      // Alert Modal
      alertModal: {
        show: false,
        title: '',
        message: '',
        variant: 'info'
      },
      // Storage Usage (admin only)
      storageUsage: {
        loading: true,
        totalStorage: 0,
        totalExports: 0,
        stationCount: 0,
        byStation: []
      }
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    }
  },
  mounted() {
    document.documentElement.classList.add('settings-page-active')
  },
  beforeUnmount() {
    document.documentElement.classList.remove('settings-page-active')
  },
  async created() {
    await this.loadStorage()
    await this.loadOpenAI()
    await this.loadGemini()
    await this.loadElevenLabs()
    await this.loadActiveAIProvider()
    await this.loadWebhooks()
    await this.loadCustomFields()
    if (this.isAdmin) {
      await this.loadStorageUsage()
    }
  },
  methods: {
    async loadStorageUsage() {
      if (!this.isAdmin) return
      this.storageUsage.loading = true
      try {
        const res = await authFetch('/api/admin/storage-usage')
        if (res.ok) {
          const data = await res.json()
          this.storageUsage.totalStorage = data.totalStorage
          this.storageUsage.totalExports = data.totalExports
          this.storageUsage.stationCount = data.stationCount
          this.storageUsage.byStation = data.byStation
        }
      } catch (err) {
        console.error('Failed to load storage usage:', err)
      } finally {
        this.storageUsage.loading = false
      }
    },
    formatSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
    },
    async loadStorage() {
      try {
        const res = await authFetch('/api/integrations/storage')
        if (res.ok) this.storage = await res.json()
      } catch (err) { console.error('Failed to load storage:', err) }
    },
    // ============ WEBHOOK METHODS ============
    async loadWebhooks() {
      try {
        this.webhooks = await getWebhooks()
      } catch (err) { console.error('Failed to load webhooks:', err) }
    },
    openWebhookModal(webhook = null) {
      if (webhook) {
        this.webhookModal = {
          show: true,
          editing: webhook,
          name: webhook.name,
          url: webhook.url,
          secret: webhook.secret || '',
          events: [...webhook.events],
          enabled: webhook.enabled
        }
      } else {
        this.webhookModal = {
          show: true,
          editing: null,
          name: '',
          url: '',
          secret: '',
          events: [],
          enabled: true
        }
      }
    },
    async saveWebhook() {
      try {
        const data = {
          name: this.webhookModal.name,
          url: this.webhookModal.url,
          secret: this.webhookModal.secret || null,
          events: this.webhookModal.events,
          enabled: this.webhookModal.enabled
        }
        if (this.webhookModal.editing) {
          await updateWebhook(this.webhookModal.editing._id, data)
        } else {
          await createWebhook(data)
        }
        this.webhookModal.show = false
        await this.loadWebhooks()
      } catch (err) {
        this.alertModal = { show: true, title: 'Error', message: 'Failed to save webhook: ' + err.message, variant: 'error' }
      }
    },
    toggleEvent(eventValue) {
      const index = this.webhookModal.events.indexOf(eventValue)
      if (index === -1) {
        this.webhookModal.events.push(eventValue)
      } else {
        this.webhookModal.events.splice(index, 1)
      }
    },
    async testWebhookAction(webhook) {
      try {
        const result = await testWebhook(webhook._id)
        if (result.success) {
          this.alertModal = { show: true, title: 'Webhook Test Successful', message: `Test webhook sent successfully! Status: ${result.status}`, variant: 'success' }
        } else {
          this.alertModal = { show: true, title: 'Webhook Test Failed', message: result.error || 'Unknown error', variant: 'error' }
        }
      } catch (err) {
        this.alertModal = { show: true, title: 'Webhook Test Failed', message: err.message, variant: 'error' }
      }
    },
    confirmDeleteWebhook(webhook) {
      this.deleteModalTitle = 'Delete Webhook'
      this.deleteModalMessage = `Are you sure you want to delete the webhook "${webhook.name}"?`
      this.deleteAction = () => this.doDeleteWebhook(webhook._id)
      this.showDeleteModal = true
    },
    async doDeleteWebhook(webhookId) {
      this.deleting = true
      try {
        this.showDeleteModal = false
        await deleteWebhook(webhookId)
        await this.loadWebhooks()
      } catch (err) {
        this.alertModal = { show: true, title: 'Error', message: 'Failed to delete webhook: ' + err.message, variant: 'error' }
      } finally { this.deleting = false }
    },
    formatEventName(event) {
      const names = {
        'export.started': 'Export Started',
        'export.complete': 'Export Complete',
        'export.failed': 'Export Failed'
      }
      return names[event] || event
    },
    formatAppliesTo(type) {
      const names = { 'exports': 'Exports' }
      return names[type] || type
    },
    // ============ CUSTOM FIELD METHODS ============
    async loadCustomFields() {
      try {
        this.customFields = await getCustomFields()
      } catch (err) { console.error('Failed to load custom fields:', err) }
    },
    openCustomFieldModal(field = null) {
      if (field) {
        this.customFieldModal = {
          show: true,
          editing: field,
          name: field.name,
          key: field.key,
          type: field.type,
          appliesToExports: Array.isArray(field.appliesTo) ? field.appliesTo.includes('exports') : true,
          required: field.required || false,
          optionsText: (field.options || []).join(', ')
        }
      } else {
        this.customFieldModal = {
          show: true,
          editing: null,
          name: '',
          key: '',
          type: 'text',
          appliesToExports: true,
          required: false,
          optionsText: ''
        }
      }
    },
    async saveCustomField() {
      try {
        const appliesTo = []
        if (this.customFieldModal.appliesToExports) appliesTo.push('exports')

        const data = {
          name: this.customFieldModal.name,
          key: this.customFieldModal.key,
          type: this.customFieldModal.type,
          appliesTo,
          required: this.customFieldModal.required
        }
        if (this.customFieldModal.type === 'dropdown') {
          data.options = this.customFieldModal.optionsText.split(',').map(o => o.trim()).filter(o => o)
        }
        if (this.customFieldModal.editing) {
          await updateCustomField(this.customFieldModal.editing._id, data)
        } else {
          await createCustomField(data)
        }
        this.customFieldModal.show = false
        await this.loadCustomFields()
      } catch (err) {
        this.alertModal = { show: true, title: 'Error', message: 'Failed to save custom field: ' + err.message, variant: 'error' }
      }
    },
    confirmDeleteCustomField(field) {
      this.deleteModalTitle = 'Delete Custom Field'
      this.deleteModalMessage = `Are you sure you want to delete the field "${field.name}"? Existing values on exports will be removed.`
      this.deleteAction = () => this.doDeleteCustomField(field._id)
      this.showDeleteModal = true
    },
    async doDeleteCustomField(fieldId) {
      this.deleting = true
      try {
        this.showDeleteModal = false
        await deleteCustomField(fieldId)
        await this.loadCustomFields()
      } catch (err) {
        this.alertModal = { show: true, title: 'Error', message: 'Failed to delete custom field: ' + err.message, variant: 'error' }
      } finally { this.deleting = false }
    },
    editProvider(provider) {
      this.editingProvider = provider
      this.testResult = null
      if (provider === 'aws' && this.storage.aws) {
        this.awsConfig = { ...this.awsConfig, bucket: this.storage.aws.bucket || '', region: this.storage.aws.region || 'us-east-1', cdnUrl: this.storage.aws.cdnUrl || '', accessKeyId: '', secretAccessKey: '' }
      } else if (provider === 'digitalocean' && this.storage.digitalocean) {
        this.doConfig = { ...this.doConfig, bucket: this.storage.digitalocean.bucket || '', region: this.storage.digitalocean.region || 'nyc3', cdnUrl: this.storage.digitalocean.cdnUrl || '', accessKeyId: '', secretAccessKey: '' }
      }
    },
    cancelEdit() { this.editingProvider = null; this.testResult = null },
    async testProvider(provider, config) {
      this.testing = true; this.testResult = null
      try {
        const res = await authFetch(`/api/integrations/storage/${provider}/test`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(config) })
        this.testResult = await res.json()
      } catch (err) { this.testResult = { success: false, message: err.message } }
      finally { this.testing = false }
    },
    async saveProvider(provider, config) {
      this.saving = true
      try {
        const res = await authFetch(`/api/integrations/storage/${provider}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...config, setActive: true }) })
        if (res.ok) { await this.loadStorage(); this.editingProvider = null; this.testResult = null }
        else { const err = await res.json(); this.testResult = { success: false, message: err.error } }
      } catch (err) { this.testResult = { success: false, message: err.message } }
      finally { this.saving = false }
    },
    async setActiveProvider(provider) {
      this.saving = true
      try {
        const res = await authFetch('/api/integrations/storage/active', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider }) })
        if (res.ok) await this.loadStorage()
      } catch (err) { console.error('Failed to set active provider:', err) }
      finally { this.saving = false }
    },
    confirmDeleteProvider(provider) {
      const names = { aws: 'Amazon S3', digitalocean: 'DigitalOcean Spaces' }
      this.deleteModalTitle = `Delete ${names[provider]} Configuration`
      this.deleteModalMessage = `Are you sure you want to delete the ${names[provider]} configuration? Existing files in your bucket will not be deleted.`
      this.deleteAction = () => this.deleteProvider(provider)
      this.showDeleteModal = true
    },
    confirmDeactivateStorage() {
      this.deleteModalTitle = 'Deactivate Cloud Storage'
      this.deleteModalMessage = 'Are you sure you want to deactivate cloud storage? New exports will be stored locally on the server.'
      this.deleteAction = () => this.setActiveProvider(null)
      this.showDeleteModal = true
    },
    async deleteProvider(provider) {
      this.deleting = true
      try {
        const res = await authFetch(`/api/integrations/storage/${provider}`, { method: 'DELETE' })
        if (res.ok) { await this.loadStorage(); this.showDeleteModal = false }
      } catch (err) { console.error('Failed to delete provider:', err) }
      finally { this.deleting = false }
    },
    async executeDelete() {
      if (this.deleteAction) { this.deleting = true; await this.deleteAction(); this.deleting = false; this.showDeleteModal = false }
    },
    async loadOpenAI() {
      try {
        const res = await authFetch('/api/integrations/openai')
        if (res.ok) {
          const data = await res.json()
          this.openai.configured = data.configured || false
          if (data.transcriptionModel) this.openai.form.transcriptionModel = data.transcriptionModel
          if (data.snippetsModel) this.openai.form.snippetsModel = data.snippetsModel
          if (!this.openai.configured) this.openai.editing = true
        }
      } catch (err) { console.error('Failed to load OpenAI:', err) }
    },
    editOpenAI() { this.openai.editing = true; this.openai.error = null; this.openai.testResult = null; this.openai.form.apiKey = '' },
    cancelOpenAIEdit() { this.openai.editing = false; this.openai.error = null; this.openai.testResult = null; this.openai.form.apiKey = ''; if (!this.openai.configured) this.openai.editing = true },
    async saveOpenAI() {
      if (!this.openai.configured && !this.openai.form.apiKey) { this.openai.error = 'API key is required'; return }
      this.openai.saving = true; this.openai.error = null
      try {
        const res = await authFetch('/api/integrations/openai', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.openai.form) })
        if (res.ok) { await this.loadOpenAI(); this.openai.editing = false; this.openai.form.apiKey = ''; await this.loadActiveAIProvider() }
        else { const err = await res.json(); this.openai.error = err.error || 'Failed to save' }
      } catch (err) { this.openai.error = err.message }
      finally { this.openai.saving = false }
    },
    async testOpenAI() {
      this.openai.testing = true; this.openai.testResult = null
      try {
        if (this.openai.form.apiKey) {
          const saveRes = await authFetch('/api/integrations/openai', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.openai.form) })
          if (!saveRes.ok) { const err = await saveRes.json(); this.openai.testResult = { success: false, error: err.error }; return }
        }
        const res = await authFetch('/api/integrations/openai/test', { method: 'POST' })
        this.openai.testResult = await res.json()
        if (this.openai.testResult.success) await this.loadOpenAI()
      } catch (err) { this.openai.testResult = { success: false, error: err.message } }
      finally { this.openai.testing = false }
    },
    confirmDeleteOpenAI() {
      this.deleteModalTitle = 'Delete OpenAI Integration'
      this.deleteModalMessage = 'Are you sure you want to delete the OpenAI integration? This will disable transcription and AI-powered features.'
      this.deleteAction = this.deleteOpenAI
      this.showDeleteModal = true
    },
    async deleteOpenAI() {
      this.deleting = true
      try {
        const res = await authFetch('/api/integrations/openai', { method: 'DELETE' })
        if (res.ok) { this.openai.configured = false; this.openai.editing = true; this.showDeleteModal = false }
      } catch (err) { console.error('Failed to delete OpenAI:', err) }
      finally { this.deleting = false }
    },

    // ── Gemini ───────────────────────────────────────────────────────────────
    async loadGemini() {
      try {
        const res = await authFetch('/api/integrations/gemini')
        const data = await res.json()
        this.gemini.configured = data.configured || false
        if (data.transcriptionModel) this.gemini.form.transcriptionModel = data.transcriptionModel
      } catch (err) { console.error('Failed to load Gemini:', err) }
    },
    editGemini() { this.gemini.editing = true; this.gemini.testResult = null; this.gemini.error = null },
    cancelGeminiEdit() { this.gemini.editing = false; this.gemini.testResult = null; this.gemini.error = null; this.gemini.form.apiKey = '' },
    async saveGemini() {
      this.gemini.saving = true; this.gemini.error = null
      try {
        const res = await authFetch('/api/integrations/gemini', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: this.gemini.form.apiKey, transcriptionModel: this.gemini.form.transcriptionModel })
        })
        const data = await res.json()
        if (data.error) throw new Error(data.error)
        this.gemini.configured = true; this.gemini.editing = false; this.gemini.form.apiKey = ''
        await this.loadActiveAIProvider()
      } catch (err) { this.gemini.error = err.message }
      finally { this.gemini.saving = false }
    },
    async testGemini() {
      this.gemini.testing = true; this.gemini.testResult = null
      try {
        const res = await authFetch('/api/integrations/gemini/test', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: this.gemini.form.apiKey || undefined })
        })
        this.gemini.testResult = await res.json()
      } catch (err) { this.gemini.testResult = { success: false, error: err.message } }
      finally { this.gemini.testing = false }
    },
    confirmDeleteGemini() {
      this.deleteModalTitle = 'Delete Gemini Integration'
      this.deleteModalMessage = 'Are you sure you want to delete the Gemini integration?'
      this.deleteAction = this.deleteGemini
      this.showDeleteModal = true
    },
    async deleteGemini() {
      this.deleting = true
      try {
        const res = await authFetch('/api/integrations/gemini', { method: 'DELETE' })
        if (res.ok) {
          this.gemini.configured = false; this.gemini.editing = true
          if (this.activeAIProvider === 'gemini') this.activeAIProvider = null
          this.showDeleteModal = false
        }
      } catch (err) { console.error('Failed to delete Gemini:', err) }
      finally { this.deleting = false }
    },

    // ── Active AI provider ───────────────────────────────────────────────────
    async loadActiveAIProvider() {
      try {
        const res = await authFetch('/api/integrations/ai/active')
        const data = await res.json()
        // If no explicit active provider but OpenAI is configured, treat it as active
        this.activeAIProvider = data.activeProvider || (this.openai.configured ? 'openai' : null)
      } catch (err) { console.error('Failed to load active AI provider:', err) }
    },
    async setActiveAIProvider(provider) {
      this.aiProviderSaving = true
      try {
        const res = await authFetch('/api/integrations/ai/active', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider })
        })
        const data = await res.json()
        if (data.error) throw new Error(data.error)
        this.activeAIProvider = data.activeProvider
      } catch (err) { console.error('Failed to set active AI provider:', err) }
      finally { this.aiProviderSaving = false }
    },

    // ── ElevenLabs ───────────────────────────────────────────────────────────
    async loadElevenLabs() {
      try {
        const res = await authFetch('/api/integrations/elevenlabs')
        const data = await res.json()
        this.elevenlabs.configured = data.configured || false
      } catch (err) { console.error('Failed to load ElevenLabs:', err) }
    },
    editElevenLabs() { this.elevenlabs.editing = true; this.elevenlabs.testResult = null; this.elevenlabs.error = null },
    cancelElevenLabsEdit() { this.elevenlabs.editing = false; this.elevenlabs.testResult = null; this.elevenlabs.error = null; this.elevenlabs.form.apiKey = '' },
    async saveElevenLabs() {
      if (!this.elevenlabs.configured && !this.elevenlabs.form.apiKey) { this.elevenlabs.error = 'API key is required'; return }
      this.elevenlabs.saving = true; this.elevenlabs.error = null
      try {
        const res = await authFetch('/api/integrations/elevenlabs', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.elevenlabs.form)
        })
        const data = await res.json()
        if (data.error) throw new Error(data.error)
        this.elevenlabs.configured = true; this.elevenlabs.editing = false; this.elevenlabs.form.apiKey = ''
      } catch (err) { this.elevenlabs.error = err.message }
      finally { this.elevenlabs.saving = false }
    },
    async testElevenLabs() {
      this.elevenlabs.testing = true; this.elevenlabs.testResult = null
      try {
        const res = await authFetch('/api/integrations/elevenlabs/test', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: this.elevenlabs.form.apiKey || undefined })
        })
        this.elevenlabs.testResult = await res.json()
        if (this.elevenlabs.testResult.success) await this.loadElevenLabs()
      } catch (err) { this.elevenlabs.testResult = { success: false, error: err.message } }
      finally { this.elevenlabs.testing = false }
    },
    confirmDeleteElevenLabs() {
      this.deleteModalTitle = 'Delete ElevenLabs Integration'
      this.deleteModalMessage = 'Are you sure you want to delete the ElevenLabs integration? This will disable voice generation.'
      this.deleteAction = this.deleteElevenLabs
      this.showDeleteModal = true
    },
    async deleteElevenLabs() {
      this.deleting = true
      try {
        const res = await authFetch('/api/integrations/elevenlabs', { method: 'DELETE' })
        if (res.ok) { this.elevenlabs.configured = false; this.elevenlabs.editing = true; this.showDeleteModal = false }
      } catch (err) { console.error('Failed to delete ElevenLabs:', err) }
      finally { this.deleting = false }
    }
  }
}
</script>

<style>
/* Override global body overflow for Settings page */
html.settings-page-active,
html.settings-page-active body,
html.settings-page-active #app {
  overflow: visible !important;
  overflow-y: auto !important;
  height: auto !important;
  min-height: 100vh;
}
</style>

<style scoped>
.settings-view { min-height: calc(100vh - 74px); background: var(--gray-50); }
.content { padding: 32px; max-width: 800px; margin: 0 auto; }
.page-title { margin-bottom: 24px; }
.page-title h1 { font-size: 24px; font-weight: 600; margin: 0 0 8px 0; color: var(--gray-900); }
.page-description { margin: 0; font-size: 14px; color: var(--gray-900); }
.tabs { display: flex; gap: 8px; margin-bottom: 24px; border-bottom: 1px solid var(--gray-200); padding-bottom: 16px; }
.tab { padding: 8px 16px; border: none; background: none; cursor: pointer; font-size: 14px; font-weight: 500; color: var(--gray-500); border-radius: 6px; display: flex; align-items: center; gap: 8px; }
.tab:hover { color: var(--gray-700); background: var(--gray-100); }
.tab.active { color: var(--accent); background: rgba(51, 147, 255, 0.1); }
.tab-content { animation: fadeIn 0.2s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.providers-list { display: flex; flex-direction: column; gap: 16px; }
.info-card { display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: #e0f2fe; border-radius: 8px; color: #0369a1; }
.info-card svg { margin-top: 2px; }
.info-card p { margin: 0; font-size: 14px; line-height: 1.5; }
.provider-card { background: white; border: 1px solid var(--gray-200); border-radius: 12px; overflow: hidden; }
.provider-card.configured { border-color: var(--gray-300); }
.provider-card.active { border-color: var(--success); border-width: 2px; }
.provider-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; }
.provider-info { display: flex; align-items: center; gap: 12px; }
.provider-icon { width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; color: white; }
.provider-icon.aws { background: linear-gradient(135deg, #ff9900, #e88a00); }
.provider-icon.do { background: linear-gradient(135deg, #0080ff, #0069cc); }
.provider-icon.ai { background: linear-gradient(135deg, #10a37f, #0d8c6d); }
.provider-icon.gemini { background: linear-gradient(135deg, #4285f4, #34a853); }
.provider-info h3 { margin: 0 0 2px 0; font-size: 15px; font-weight: 600; }
.provider-desc { margin: 0; font-size: 13px; color: var(--gray-500); }
.badge { padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 500; }
.badge.active { background: #d1fae5; color: #059669; }
.badge.configured { background: var(--gray-100); color: var(--gray-600); }
.provider-body { padding: 0 20px 20px; }
.provider-form { padding-top: 16px; border-top: 1px solid var(--gray-100); }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 13px; font-weight: 500; color: var(--gray-700); margin-bottom: 6px; }
.form-group .optional { font-weight: 400; color: var(--gray-400); }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-input, .form-select { width: 100%; padding: 10px 12px; border: 1px solid var(--gray-300); border-radius: 6px; font-size: 14px; }
.form-input:focus, .form-select:focus { outline: none; border-color: var(--accent); }
textarea.form-input { resize: vertical; font-family: monospace; font-size: 12px; }
.form-select { background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 12px center; appearance: none; cursor: pointer; }
.form-hint { margin-top: 6px; font-size: 12px; color: var(--gray-500); display: flex; align-items: center; gap: 6px; }
.form-hint.success { color: var(--success); }
.form-error { margin-top: 12px; padding: 10px; background: #fef2f2; border-radius: 6px; color: #dc2626; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.input-with-toggle { position: relative; display: flex; }
.input-with-toggle .form-input { padding-right: 40px; }
.btn-toggle-visibility { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--gray-400); cursor: pointer; padding: 4px; }
.btn-toggle-visibility:hover { color: var(--gray-600); }
.form-actions { display: flex; gap: 8px; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--gray-100); }
.provider-actions { display: flex; gap: 8px; padding-top: 12px; }
.btn-primary, .btn-secondary, .btn-danger { padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; }
.btn-primary { background: var(--accent); color: white; }
.btn-primary:hover:not(:disabled) { background: var(--accent-hover); }
.btn-secondary { background: white; color: var(--gray-700); border: 1px solid var(--gray-300); }
.btn-secondary:hover:not(:disabled) { background: var(--gray-50); }
.btn-danger { background: #dc2626; color: white; }
.btn-danger:hover:not(:disabled) { background: #b91c1c; }
.btn-primary:disabled, .btn-secondary:disabled, .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-text { background: none; border: none; color: var(--gray-500); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; padding: 8px 0; }
.btn-text:hover { color: var(--gray-700); }
.test-result { margin-top: 12px; padding: 10px; border-radius: 6px; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.test-result.success { background: #d1fae5; color: #059669; }
.test-result.error { background: #fef2f2; color: #dc2626; }
.deactivate-section { text-align: center; padding-top: 8px; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 2000; backdrop-filter: blur(2px); }
.modal { background: white; border-radius: 12px; width: 100%; max-width: 420px; box-shadow: var(--shadow-xl); }
.modal-header { padding: 16px 20px; border-bottom: 1px solid var(--gray-100); display: flex; align-items: center; justify-content: space-between; }
.modal-header h3 { font-size: 16px; font-weight: 600; margin: 0; }
.close-btn { background: none; border: none; color: var(--gray-400); cursor: pointer; padding: 4px; }
.close-btn:hover { color: var(--gray-600); }
.modal-body { padding: 20px; }
.modal-body p { margin: 0; color: var(--gray-600); line-height: 1.5; }
.modal-footer { padding: 16px 20px; border-top: 1px solid var(--gray-100); display: flex; justify-content: flex-end; gap: 8px; }

/* Section Header */
.section-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; }
.section-title { font-size: 18px; font-weight: 600; color: #1f2937; margin: 0; display: flex; align-items: center; gap: 6px; }
.section-desc { color: #6b7280; font-size: 14px; margin: 4px 0 0 0; }

/* Help Icon */
.help-icon { background: transparent; border: none; padding: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.help-icon-circle { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid #9ca3af; color: #9ca3af; font-size: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center; line-height: 1; }
.help-icon:hover .help-icon-circle { border-color: #6b7280; color: #6b7280; }

/* Empty State */
.empty-state { text-align: center; padding: 48px 24px; background: #f9fafb; border-radius: 8px; color: #6b7280; }
.empty-state svg { color: #d1d5db; margin-bottom: 16px; }
.empty-state p { margin: 0; font-size: 15px; color: #374151; }
.empty-hint { font-size: 14px !important; margin-top: 8px !important; color: #9ca3af !important; }

/* Webhook/Custom Field Cards */
.webhooks-list { display: flex; flex-direction: column; gap: 16px; }
.webhook-card { background: white; border: 1px solid var(--gray-200); border-radius: 8px; padding: 16px 20px; }
.webhook-header { display: flex; justify-content: space-between; align-items: flex-start; }
.webhook-info h4 { margin: 0; font-size: 15px; font-weight: 600; color: #1f2937; }
.webhook-url { margin: 4px 0 0 0; font-size: 14px; color: #6b7280; }
.webhook-actions { display: flex; align-items: center; gap: 4px; }
.webhook-events { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.field-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.event-tag { background: #f3f4f6; color: #4b5563; padding: 4px 12px; border-radius: 12px; font-size: 13px; font-weight: 400; }

/* Icon Buttons */
.btn-icon { background: transparent; border: none; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #9ca3af; border-radius: 4px; transition: all 0.15s; }
.btn-icon:hover { background: #f3f4f6; color: #6b7280; }
.btn-icon svg { flex-shrink: 0; }

/* Badges */
.badge.enabled { background: #dcfce7; color: #166534; }
.badge.disabled { background: #f3f4f6; color: #6b7280; }
.badge.type-badge { background: #e0e7ff; color: #4338ca; }
.badge.required-badge { background: #fee2e2; color: #dc2626; }

/* Settings Modal */
.settings-modal { background: white; border-radius: 12px; width: 100%; max-width: 520px; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: var(--shadow-xl); }
.settings-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--gray-200); }
.settings-modal-title { display: flex; align-items: center; gap: 12px; }
.settings-modal-title svg { color: #6b7280; flex-shrink: 0; }
.settings-modal-title h2 { margin: 0; font-size: 18px; font-weight: 600; color: #1f2937; }
.settings-modal-close { background: transparent; border: none; padding: 8px; cursor: pointer; color: #9ca3af; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
.settings-modal-close:hover { background: #f3f4f6; color: #6b7280; }
.settings-modal-body { padding: 24px; overflow-y: auto; flex: 1; }
.settings-modal-footer { display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid var(--gray-200); background: var(--gray-50); }

/* Settings Form */
.settings-form-group { margin-bottom: 20px; }
.settings-form-group label { display: block; font-weight: 500; margin-bottom: 6px; color: var(--gray-700); }
.settings-form-group label .optional { font-weight: 400; color: var(--gray-400); }
.settings-form-group input, .settings-form-group select { width: 100%; padding: 10px 12px; border: 1px solid var(--gray-300); border-radius: 6px; font-size: 0.95rem; }
.settings-form-group input:focus, .settings-form-group select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
.settings-form-group input:disabled { background: #f3f4f6; color: #9ca3af; }
.settings-help-text { margin: 8px 0 0 0; font-size: 13px; color: var(--gray-500); }

/* Toggle/Checkbox */
.settings-toggle-section { margin-bottom: 20px; padding: 16px; background: var(--gray-50); border-radius: 8px; }
.settings-toggle-hint { margin: 8px 0 0 30px; font-size: 13px; color: var(--gray-500); }
.settings-checkbox-item { display: flex; align-items: center; gap: 12px; cursor: pointer; font-weight: 400; font-size: 15px; color: #374151; }
.settings-checkbox-item:hover .custom-checkbox:not(.checked) { border-color: #9ca3af; }
.custom-checkbox { width: 18px; height: 18px; min-width: 18px; border: 2px solid #d1d5db; border-radius: 4px; background: white; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease; flex-shrink: 0; }
.custom-checkbox.checked { background: var(--accent); border-color: var(--accent); }
.custom-checkbox svg { width: 12px; height: 12px; color: white; }
.settings-checkbox-item span { line-height: 1.2; user-select: none; }
.settings-checkbox-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px 24px; margin-top: 12px; }
.applies-to-checkboxes { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 8px; }

/* Payload Docs */
.payload-docs-modal { max-width: 640px; }
.payload-docs-body { max-height: 60vh; }
.payload-intro { margin: 0 0 20px 0; color: var(--gray-600); line-height: 1.6; }
.payload-intro code { background: var(--gray-100); padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
.payload-section { margin-bottom: 24px; }
.payload-section h4 { margin: 0 0 12px 0; font-size: 1rem; font-weight: 600; color: var(--gray-800); }
.payload-event { margin-bottom: 16px; }
.payload-event .event-tag { margin-right: 8px; margin-bottom: 8px; }
.payload-event pre { margin: 8px 0 0 0; background: #1a1a2e; border-radius: 6px; padding: 16px; overflow-x: auto; }
.payload-event code { color: #e2e8f0; font-size: 0.85rem; line-height: 1.5; }

/* Alert Modal */
.alert-modal { max-width: 400px; width: 100%; background: white; border-radius: 12px; position: relative; box-shadow: var(--shadow-lg); }
.alert-modal-close { position: absolute; top: 16px; right: 16px; background: none; border: none; padding: 4px; cursor: pointer; color: #9ca3af; border-radius: 4px; z-index: 1; }
.alert-modal-close:hover { background: #f3f4f6; color: #6b7280; }
.alert-modal-content { padding: 40px 32px 32px; text-align: center; }
.alert-modal-icon { width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
.alert-modal-icon.success { background: #d1fae5; color: #059669; }
.alert-modal-icon.error { background: #fee2e2; color: #dc2626; }
.alert-modal-icon.info { background: #dbeafe; color: #2563eb; }
.alert-modal-title { font-size: 20px; font-weight: 600; color: #1f2937; margin: 0 0 8px 0; }
.alert-modal-message { font-size: 15px; color: #6b7280; margin: 0 0 24px 0; line-height: 1.5; }
.alert-modal-btn { padding: 10px 32px; font-size: 15px; }

/* Usage Tab */
.provider-icon.usage {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  font-size: 18px;
}
.provider-icon.stations {
  background: linear-gradient(135deg, #10b981, #059669);
  font-size: 18px;
}
.usage-stats-row {
  display: flex;
  gap: 48px;
  padding: 8px 0;
}
.usage-stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.usage-stat-item .usage-stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}
.usage-stat-item .usage-stat-label {
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.usage-table {
  width: 100%;
  border-collapse: collapse;
}
.usage-table th,
.usage-table td {
  padding: 12px 20px;
  text-align: left;
  border-bottom: 1px solid var(--gray-100);
}
.usage-table th {
  background: var(--gray-50);
  font-weight: 600;
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.usage-table td {
  font-size: 14px;
  color: #374151;
}
.usage-table tr:last-child td {
  border-bottom: none;
}
.usage-table tr:hover td {
  background: var(--gray-50);
}
.station-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}
.station-link:hover {
  text-decoration: underline;
}
.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.ai-section-header {
  margin-bottom: 16px;
}
.ai-section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--gray-800);
  margin: 0 0 4px;
  letter-spacing: 0.01em;
}
.ai-section-desc {
  font-size: 12px;
  color: var(--gray-500);
  margin: 0;
}
.ai-section-divider {
  border: none;
  border-top: 1px solid var(--gray-200);
  margin: 28px 0 24px;
}
.provider-icon.elevenlabs {
  background: linear-gradient(135deg, #f5a623, #e8820c);
  color: white;
  font-size: 11px;
  font-weight: 800;
}

</style>
